/**
 * DOMI-CARE - Cliente de Comunicación con Google Apps Script y Sincronizador
 * Maneja llamadas REST HTTP, detección de conectividad, cola offline y reintentos.
 */

import { offlineStore } from './offlineStore.js';

class AppsScriptClient {
  constructor() {
    this.apiUrl = '';
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.listeners = [];

    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyStatusChange();
      this.syncPendingQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyStatusChange();
    });

    this.init();
  }

  async init() {
    this.apiUrl = await offlineStore.getSetting('appsScriptUrl', '');
  }

  setApiUrl(url) {
    this.apiUrl = url ? url.trim() : '';
    offlineStore.setSetting('appsScriptUrl', this.apiUrl);
    this.notifyStatusChange();
  }

  getApiUrl() {
    return this.apiUrl;
  }

  hasValidConnection() {
    return this.isOnline && !!this.apiUrl && this.apiUrl.startsWith('https://script.google.com');
  }

  onStatusChange(cb) {
    this.listeners.push(cb);
  }

  notifyStatusChange() {
    this.listeners.forEach(cb => cb({
      isOnline: this.isOnline,
      hasUrl: !!this.apiUrl,
      mode: this.hasValidConnection() ? 'live' : 'demo'
    }));
  }

  // ==========================================
  // OPERACIONES DE DATOS
  // ==========================================

  async getPacientes() {
    // Si estamos en modo live y online, podemos refrescar desde Apps Script
    if (this.hasValidConnection()) {
      try {
        const res = await fetch(`${this.apiUrl}?action=getInitialData`);
        const json = await res.json();
        if (json.success && json.data && json.data.pacientes) {
          // Guardar en cache local
          await offlineStore.bulkInsert('pacientes', json.data.pacientes);
          return json.data.pacientes;
        }
      } catch (err) {
        console.warn('Fallo al conectar con Google Sheets, usando base local IndexedDB:', err);
      }
    }
    return offlineStore.getAll('pacientes');
  }

  async saveEvolucion(evolucionData, signosVitalesData, idVisitaAgenda, activeUser) {
    // 1. Guardar inmediatamente en base local IndexedDB para respuesta instantánea
    const idEvolucion = 'EVO-' + Math.floor(1000 + Math.random() * 9000);
    const fechaHora = new Date().toISOString().replace('T', ' ').substring(0, 16);

    const record = {
      id: idEvolucion,
      patientId: evolucionData.patientId,
      fechaHora: fechaHora,
      prestadorNombre: activeUser.nombre,
      disciplina: activeUser.rol,
      matricula: activeUser.matricula || 'MN-REG',
      subjetivoObjetivo: evolucionData.subjetivoObjetivo,
      evaluacionDiagnostica: evolucionData.evaluacionDiagnostica,
      planTratamiento: evolucionData.planTratamiento,
      practicasRealizadas: evolucionData.practicasRealizadas,
      firmas: {
        profesional: evolucionData.firmaProfesional,
        familiar: evolucionData.firmaFamiliar,
        aclaracionFamiliar: evolucionData.aclaracionFamiliar
      },
      gps: evolucionData.gps || 'Captura manual'
    };

    await offlineStore.put('evoluciones', record);

    // Si incluyó signos vitales
    if (signosVitalesData && (signosVitalesData.sistolica || signosVitalesData.temperatura)) {
      const svRecord = {
        id: 'SV-' + Math.floor(1000 + Math.random() * 9000),
        patientId: evolucionData.patientId,
        fechaHora: fechaHora,
        sistolica: Number(signosVitalesData.sistolica) || null,
        diastolica: Number(signosVitalesData.diastolica) || null,
        frecuenciaCardiaca: Number(signosVitalesData.frecuenciaCardiaca) || null,
        saturacionO2: Number(signosVitalesData.saturacionO2) || null,
        temperatura: Number(signosVitalesData.temperatura) || null,
        glucemia: Number(signosVitalesData.glucemia) || null,
        dolorEVA: Number(signosVitalesData.dolorEVA) || 0,
        observaciones: signosVitalesData.observaciones || '',
        idEvolucion: idEvolucion
      };
      await offlineStore.put('signos', svRecord);
    }

    // Si viene de una visita de la agenda, marcarla realizada localmente
    if (idVisitaAgenda) {
      const visita = await offlineStore.getById('agenda', idVisitaAgenda);
      if (visita) {
        visita.estado = 'Realizada';
        visita.horaEfectiva = new Date().toTimeString().substring(0, 5);
        visita.gps = evolucionData.gps;
        await offlineStore.put('agenda', visita);
      }
    }

    // 2. Gestionar la transmisión o encolado
    const payloadParaBackend = {
      action: 'saveEvolucion',
      id: idEvolucion,
      patientId: evolucionData.patientId,
      providerId: activeUser.id,
      userEmail: activeUser.email,
      userRole: activeUser.rol,
      fechaHora: fechaHora,
      disciplina: activeUser.rol,
      subjetivoObjetivo: evolucionData.subjetivoObjetivo,
      evaluacionDiagnostica: evolucionData.evaluacionDiagnostica,
      planTratamiento: evolucionData.planTratamiento,
      practicasRealizadas: evolucionData.practicasRealizadas,
      firmaProfesional: evolucionData.firmaProfesional,
      firmaFamiliar: evolucionData.firmaFamiliar,
      dniFamiliar: evolucionData.aclaracionFamiliar,
      gpsRegistro: evolucionData.gps,
      idVisitaAgenda: idVisitaAgenda,
      signosVitales: signosVitalesData
    };

    if (this.hasValidConnection()) {
      try {
        await this.postToAppsScript(payloadParaBackend);
        return { success: true, idEvolucion, synced: true };
      } catch (err) {
        console.warn('Error al transmitir al servidor, encolando offline:', err);
        await offlineStore.enqueueAction('saveEvolucion', payloadParaBackend);
        return { success: true, idEvolucion, synced: false, enqueued: true };
      }
    } else {
      // Modo offline o demo: encolar
      await offlineStore.enqueueAction('saveEvolucion', payloadParaBackend);
      return { success: true, idEvolucion, synced: false, enqueued: true };
    }
  }

  async saveEscala(escalaData, activeUser) {
    const idEscala = 'ESC-' + Math.floor(1000 + Math.random() * 9000);
    const fechaHora = new Date().toISOString().split('T')[0];

    const localRecord = {
      id: idEscala,
      patientId: escalaData.patientId,
      fechaHora: fechaHora,
      tipo: escalaData.tipo,
      puntaje: escalaData.puntaje,
      clasificacion: escalaData.clasificacion,
      color: escalaData.color,
      detalles: escalaData.detalles
    };

    await offlineStore.put('escalas', localRecord);

    const payload = {
      action: 'saveEscala',
      id: idEscala,
      patientId: escalaData.patientId,
      userEmail: activeUser.email,
      userRole: activeUser.rol,
      tipoEscala: escalaData.tipo,
      puntajeTotal: escalaData.puntaje,
      clasificacionRiesgo: escalaData.clasificacion,
      respuestas: escalaData.detalles,
      prestadorEvaluador: activeUser.nombre
    };

    if (this.hasValidConnection()) {
      try {
        await this.postToAppsScript(payload);
        return { success: true, idEscala, synced: true };
      } catch (e) {
        await offlineStore.enqueueAction('saveEscala', payload);
        return { success: true, idEscala, synced: false, enqueued: true };
      }
    } else {
      await offlineStore.enqueueAction('saveEscala', payload);
      return { success: true, idEscala, synced: false, enqueued: true };
    }
  }

  async saveIncidente(incidenteData, activeUser) {
    const idIncidente = 'INC-' + Math.floor(1000 + Math.random() * 9000);
    const fechaHora = new Date().toISOString().replace('T', ' ').substring(0, 16);

    const localRecord = {
      id: idIncidente,
      patientId: incidenteData.patientId,
      pacienteNombre: incidenteData.pacienteNombre,
      fechaHora: fechaHora,
      tipo: incidenteData.tipo,
      severidad: incidenteData.severidad,
      descripcion: incidenteData.descripcion,
      acciones: incidenteData.acciones,
      reportante: activeUser.nombre,
      estado: 'En Investigación / Comité'
    };

    await offlineStore.put('incidentes', localRecord);

    const payload = {
      action: 'saveIncidente',
      id: idIncidente,
      patientId: incidenteData.patientId,
      userEmail: activeUser.email,
      userRole: activeUser.rol,
      tipoEvento: incidenteData.tipo,
      severidad: incidenteData.severidad,
      descripcion: incidenteData.descripcion,
      accionesInmediatas: incidenteData.acciones
    };

    if (this.hasValidConnection()) {
      try {
        await this.postToAppsScript(payload);
        return { success: true, idIncidente, synced: true };
      } catch (e) {
        await offlineStore.enqueueAction('saveIncidente', payload);
        return { success: true, idIncidente, synced: false };
      }
    } else {
      await offlineStore.enqueueAction('saveIncidente', payload);
      return { success: true, idIncidente, synced: false };
    }
  }

  async saveDictamenAuditoria(idAuditoria, estado, observaciones, activeUser) {
    const auditItem = await offlineStore.getById('auditoria', idAuditoria);
    if (auditItem) {
      auditItem.estado = estado;
      auditItem.auditor = activeUser.nombre;
      auditItem.dictamenFecha = new Date().toISOString().split('T')[0];
      auditItem.observaciones = observaciones;
      await offlineStore.put('auditoria', auditItem);
    }

    const payload = {
      action: 'saveDictamenAuditoria',
      idAuditoria,
      estadoAuditoria: estado,
      observaciones,
      userEmail: activeUser.email,
      userRole: activeUser.rol
    };

    if (this.hasValidConnection()) {
      try {
        await this.postToAppsScript(payload);
      } catch (e) {
        await offlineStore.enqueueAction('saveDictamenAuditoria', payload);
      }
    } else {
      await offlineStore.enqueueAction('saveDictamenAuditoria', payload);
    }

    return { success: true };
  }

  // ==========================================
  // SINCRONIZACIÓN DE LA COLA OFFLINE
  // ==========================================

  async syncPendingQueue() {
    if (this.isSyncing) return;
    const pendingItems = await offlineStore.getPendingQueue();
    if (pendingItems.length === 0) return;

    if (!this.hasValidConnection()) {
      console.log(`DOMI-CARE: Hay ${pendingItems.length} registros en cola local esperando conexión a Apps Script.`);
      return;
    }

    this.isSyncing = true;
    console.log(`DOMI-CARE: Sincronizando ${pendingItems.length} registros pendientes con Google Sheets...`);

    try {
      const payload = {
        action: 'syncBatch',
        items: pendingItems.map(p => ({ action: p.action, payload: p.payload })),
        userEmail: 'sincro_pwa@domicare.com',
        userRole: 'SyncEngine'
      };

      const res = await this.postToAppsScript(payload);
      if (res && res.success) {
        await offlineStore.clearQueue();
        console.log('DOMI-CARE: Sincronización offline completada con éxito.');
        window.dispatchEvent(new CustomEvent('domicare:sync-completed', { detail: { count: pendingItems.length } }));
      }
    } catch (err) {
      console.error('Error al sincronizar cola con Apps Script:', err);
    } finally {
      this.isSyncing = false;
    }
  }

  async postToAppsScript(data) {
    if (!this.apiUrl) throw new Error('URL de Google Apps Script no configurada');

    // Google Apps Script requiere text/plain en fetch para evitar preflight CORS en ciertos navegadores
    const response = await fetch(this.apiUrl, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      }
    });

    return await response.json();
  }
}

export const appsScriptClient = new AppsScriptClient();
